import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, LineChart, ExternalLink, FlaskConical } from "lucide-react";
import { ContractBadge, CONTRACT_ADDRESS } from "@/components/ContractBadge";

// Pump.fun coin URL (BUY button + branding)
const PUMPFUN_URL = `https://pump.fun/coin/${CONTRACT_ADDRESS}`;
// GeckoTerminal pool address — pump.fun blocks iframes via X-Frame-Options,
// so we embed GeckoTerminal which indexes the same pump.fun bonding-curve
// trades and renders an identical OHLCV chart.
const GT_POOL_ADDRESS = "8YZkqSgjMidqYDa2ztTGMkfF6gmK5U6Vek4Q8D1227Wb";
const isLaunched = CONTRACT_ADDRESS && CONTRACT_ADDRESS !== "TBA" && CONTRACT_ADDRESS.length > 10;

const quickFacts = [
  { k: "TICKER",         v: "$PAUL" },
  { k: "CONTRACT",       v: null   }, // rendered via <ContractBadge />
  { k: "CHAIN",          v: "Solana" },
  { k: "FIRST CONTACT",  v: "1947, Wyoming" },
  { k: "STATUS",         v: "Finally on the run" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white-2">
        <div className="dots-bg" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid md:grid-cols-12 gap-10 relative">
          <div className="md:col-span-7 fade-up relative z-10">
            <div className="inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-white">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"/>
              LIVE TRANSMISSION · RURAL NEVADA · 03:47 MST
            </div>

            <h1 className="font-neon text-[72px] sm:text-[100px] lg:text-[148px] leading-[0.88] text-white glow-white flicker mt-5">
              <span className="font-mono font-extrabold align-top text-[0.6em] mr-2">$</span>PAUL
            </h1>
            <div className="font-type text-white/90 text-2xl mt-1">
              the alien who finally logged on.
            </div>
            <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase border border-white px-3 py-1.5 bg-ink/40 text-white">
              <span className="text-green">◉</span> $PAUL · MEMECOIN · SOLANA
            </div>

            <p className="font-type text-white text-lg mt-8 max-w-xl leading-relaxed">
              Sixty years at Area 51, zero decent cigarettes, and one hell of a Wi-Fi signal. I'm
              done being classified. This is my corner of the internet — the memes, the manifesto,
              the receipts your government doesn't want you reading. Pull up a folding chair, Earthling.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-ctas">
              <Link to="/manifesto" className="btn-paul" data-testid="cta-manifesto">
                Read the Manifesto <ArrowRight size={14} />
              </Link>
              <Link to="/tokenomics" className="btn-paul green" data-testid="cta-tokenomics">
                Tokenomics <ArrowRight size={14} />
              </Link>
              <Link to="/meme" className="btn-paul purple" data-testid="cta-meme-lab">
                <FlaskConical size={14} /> Meme Lab <ArrowRight size={14} />
              </Link>
              <Link to="/vault" className="btn-paul outline" data-testid="cta-vault">
                Open the Vault <ArrowRight size={14} />
              </Link>
              <Link to="/news" className="btn-paul purple" data-testid="cta-disclosure">
                Disclosure <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative flex items-center justify-center">
            <div className="relative">
              <div className="pfp-frame float">
                <img
                  src="/paul.jpg"
                  alt="Paul the alien"
                  className="block w-full max-w-[420px]"
                  data-testid="hero-paul-image"
                />
              </div>
              <div className="mt-5 font-mono text-[11px] tracking-[0.3em] text-white/80 text-center">
                SUBJ. P-01 · GRAY · M · 5'2"
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK FACTS STRIP */}
      <section className="bg-ink border-t border-b border-white-2">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-5 gap-6" data-testid="facts-strip">
          {quickFacts.map((f) => (
            <div key={f.k} className="flex flex-col">
              <span className="font-mono text-[10px] tracking-[0.25em] text-green">{f.k}</span>
              {f.k === "CONTRACT" ? (
                <span className="mt-1">
                  <ContractBadge size="md" dataTestid="hq-contract-address" />
                </span>
              ) : (
                <span className="font-type text-white text-xl mt-1">{f.v}</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* QUOTE BOARD */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-mono text-xs text-green tracking-[0.3em]">FILE / 01 · WHO IS PAUL</div>
          <h2 className="font-neon text-4xl md:text-5xl text-white mt-3 glow-white">A gray alien<br/>with great taste<br/>in cigarettes.</h2>
          <p className="font-type text-white/90 text-base mt-6 leading-relaxed">
            Crashed in Wyoming in 1947. Spent six decades consulting on every major sci-fi
            franchise you love — uncredited, obviously. Escaped a secret USAF facility in 2011.
            Currently hiding in plain sight, posting like a maniac, and minting on Solana.
          </p>
          <Link to="/manifesto" className="btn-paul mt-8" data-testid="read-more-btn">
            The whole story <ArrowRight size={14} />
          </Link>
        </div>
        <div className="md:col-span-7 space-y-5">
          {[
            "I'm not a secret. I'm a leak.",
            "You want disclosure? Scroll down.",
            "I've been probed enough times to sue, but I'm chill."
          ].map((q, i) => (
            <div key={i} className="paper-card px-7 py-5" style={{ transform: `rotate(${i%2===0?'-0.6deg':'0.4deg'})` }}>
              <Quote size={18} className="text-ink/50" />
              <div className="font-type text-ink text-xl mt-1">{q}</div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-ink/60 mt-2">— @alienpaul_sol</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE PUMP.FUN CHART */}
      <section className="max-w-7xl mx-auto px-6 pb-8" data-testid="live-chart-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
          <div>
            <div className="font-mono text-xs tracking-[0.3em] text-green flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: isLaunched ? "var(--p-green-2)" : "var(--p-red)", animation: "pulse 2s ease-in-out infinite" }} />
              LIVE CHART {isLaunched ? "· PUMP.FUN" : "· AWAITING LAUNCH"}
            </div>
            <h2 className="font-neon text-3xl md:text-4xl text-white glow-white mt-2 flex items-center gap-3">
              <LineChart size={30} className="text-green" />
              $PAUL / SOL
            </h2>
          </div>

          {isLaunched && (
            <a
              href={PUMPFUN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-paul green"
              data-testid="buy-paul-btn"
            >
              <ExternalLink size={14} /> BUY $PAUL
            </a>
          )}
        </div>

        <div
          className="relative border-2 border-white bg-ink overflow-hidden"
          style={{ borderRadius: 6, boxShadow: "6px 6px 0 0 rgba(0,0,0,0.6)" }}
          data-testid="pumpfun-chart-frame"
        >
          {/* fake terminal header */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-black/60 border-b border-white/15">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-white/80 uppercase">
              <span className="w-2 h-2 rounded-full" style={{ background: "#FF5F57" }} />
              <span className="w-2 h-2 rounded-full" style={{ background: "#FEBC2E" }} />
              <span className="w-2 h-2 rounded-full" style={{ background: "#28C840" }} />
              <span className="ml-2">pump.fun / coin / $paul</span>
            </div>
            <span className="font-mono text-[10px] tracking-[0.25em] text-white/70 uppercase">
              {isLaunched ? "live feed · ws open" : "signal pending"}
            </span>
          </div>

          {isLaunched ? (
            <iframe
              src={`https://www.geckoterminal.com/solana/pools/${GT_POOL_ADDRESS}?embed=1&info=0&swaps=0&grayscale=0&light_chart=0`}
              title="$PAUL live chart"
              className="w-full block bg-black"
              style={{ height: "620px", border: 0 }}
              data-testid="pumpfun-chart-iframe"
            />
          ) : (
            <div
              className="relative flex flex-col items-center justify-center text-center px-6"
              style={{ height: "420px" }}
              data-testid="dex-chart-placeholder"
            >
              {/* faux grid + scanning line */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(127,255,58,0.08) 1px, transparent 1px)," +
                    "linear-gradient(90deg, rgba(127,255,58,0.08) 1px, transparent 1px)",
                  backgroundSize: "40px 40px, 40px 40px",
                  maskImage: "radial-gradient(circle at center, black 40%, transparent 85%)",
                }}
              />
              <div
                className="absolute left-0 right-0 h-px"
                style={{
                  top: "50%",
                  background: "linear-gradient(90deg, transparent, rgba(46,200,122,0.85), transparent)",
                  boxShadow: "0 0 12px rgba(46,200,122,0.6)",
                  animation: "pulse 2.4s ease-in-out infinite",
                }}
              />
              <LineChart size={40} className="text-green relative z-10" />
              <div className="font-type text-white text-2xl mt-4 relative z-10">Chart goes live at launch.</div>
              <div className="font-mono text-[11px] tracking-[0.3em] text-white/70 mt-3 uppercase relative z-10">
                The moment $PAUL mints on Solana, this fills with real trades.
              </div>
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 border border-white/40 font-mono text-[11px] tracking-[0.3em] text-white/85 uppercase relative z-10">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--p-red)", animation: "pulse 1.2s ease-in-out infinite" }} />
                Awaiting transmission · contract TBA
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PANELS */}
      <section className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-6" data-testid="panels">
        {[
          { to:"/tokenomics", title:"TOKENOMICS", desc:"Supply, allocation, whitepaper. The paperwork on Paul." },
          { to:"/vault",      title:"UAP VAULT",  desc:"12 declassified cases. FBI, NSA, NASA, DoD. All sourced." },
          { to:"/news",       title:"DISCLOSURE", desc:"Live-style feed of verified gov posts about the real story." },
        ].map((p) => (
          <Link
            key={p.to}
            to={p.to}
            className="group relative block p-7 border border-white bg-ink/50 hover:bg-ink/80 transition-colors overflow-hidden"
            data-testid={`panel-${p.title.toLowerCase().replace(' ','-')}`}
            style={{borderRadius:4, boxShadow:'6px 6px 0 0 rgba(0,0,0,0.5)'}}
          >
            <div className="font-mono text-[11px] tracking-[0.3em] text-green">
              {p.title}
            </div>
            <div className="font-type text-white text-2xl mt-4">{p.desc}</div>
            <div className="mt-6 inline-flex items-center gap-2 font-mono text-xs text-white/80 group-hover:text-green">
              enter <ArrowRight size={13} />
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}
